<template>
  <div class="card-carousel">
    <div class="card-carousel__header">
      <span class="typo-lg-bold">다음 구매 예상일</span>
      <div class="card-carousel__info-wrap">
        <img
          class="card-carousel__info-icon"
          src="@/assets/home/icn_info_circle.png"
          alt="info"
          @click="showTooltip = !showTooltip"
        />
        <div v-if="showTooltip" class="card-carousel__tooltip">
          <span class="typo-sm-medium-white">{{ tooltipText }}</span>
          <div class="card-carousel__tooltip-arrow"></div>
        </div>
      </div>
    </div>

    <template v-if="items.length > 0">
      <div
        ref="viewport"
        class="card-carousel__viewport"
        @touchstart="items.length > 1 && onDragStart($event)"
        @touchmove="items.length > 1 && onDragMove($event)"
        @touchend="items.length > 1 && onDragEnd($event)"
        @mousedown="items.length > 1 && onMouseDown($event)"
      >
        <div
          class="card-carousel__track"
          :style="trackStyle"
        >
          <div
            v-for="(item, index) in items"
            :key="index"
            class="card-carousel__card"
            :style="cardWidth ? { width: cardWidth + 'px' } : {}"
          >
            <div class="card-carousel__card-top">
              <span class="typo-md-bold card-carousel__name">{{ item.name }}</span>
            </div>
            <div
              v-for="(purchase, pIndex) in item.purchases"
              :key="pIndex"
              class="card-carousel__purchase"
            >
              <span class="card-carousel__type">{{ convertType(purchase.type) }}</span>
              <div class="card-carousel__purchase-right">
                <span :class="getDdayClass(purchase.date)">{{ getDday(purchase.date) }}</span>
                <span class="card-carousel__date">{{ formatDate(purchase.date) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DotGroup
        v-if="items.length > 1"
        :count="items.length"
        :activeIndex="activeIndex"
      />
    </template>

    <div v-else class="card-carousel__empty" @click="$emit('empty-click')">
      <img
        class="card-carousel__empty-icon"
        src="@/assets/home/icn_calendar_3d.png"
        alt="calendar"
      />
      <span class="typo-md-bold">솔닥컨시어지로 다음 구매시점을<br/>관리해 보세요!</span>
    </div>
  </div>
</template>

<script>
import DotGroup from '@/components/new/molecules/DotGroup.vue'
import Convert from '@/utilities/convert'

export default {
  name: 'CardCarousel',
  components: {
    DotGroup,
  },
  props: {
    items: {
      type: Array,
      default: function () {
        return []
      },
    },
    tooltipText: {
      type: String,
      default: '솔닥 결제 기준으로 계산한 날짜예요.\n정확한 날짜는 공단 문자로 확인해 주세요.',
    },
  },
  data: function () {
    return {
      showTooltip: false,
      activeIndex: 0,
      startX: 0,
      deltaX: 0,
      isDragging: false,
      cardWidth: 0,
      viewportWidth: 0,
      gap: 8,
      sidePeek: 0,
      sidePadding: 16,
    }
  },
  computed: {
    trackStyle: function () {
      var offset = this.sidePadding - this.activeIndex * (this.cardWidth + this.gap)
      var drag = this.isDragging ? this.deltaX : 0
      return {
        transform: 'translateX(' + (offset + drag) + 'px)',
        transition: this.isDragging ? 'none' : 'transform 0.3s ease',
      }
    },
  },
  watch: {
    items: function () {
      if (this.activeIndex > Math.max(0, this.items.length - 1)) {
        this.activeIndex = 0
      }
      this.$nextTick(this.updateCardWidth)
    },
  },
  mounted: function () {
    this.updateCardWidth()
    window.addEventListener('resize', this.updateCardWidth)
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.updateCardWidth)
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    updateCardWidth: function () {
      if (this.$refs.viewport) {
        this.viewportWidth = this.$refs.viewport.offsetWidth
        if (this.items.length <= 1) {
          this.cardWidth = this.viewportWidth - this.sidePadding * 2
        } else {
          this.cardWidth = this.viewportWidth - this.sidePadding * 2 - 40
        }
      }
    },
    onDragStart: function (e) {
      this.startX = e.touches[0].clientX
      this.isDragging = true
      this.deltaX = 0
    },
    onDragMove: function (e) {
      if (!this.isDragging) return
      this.deltaX = e.touches[0].clientX - this.startX
    },
    onDragEnd: function () {
      this.finishDrag()
    },
    onMouseDown: function (e) {
      e.preventDefault()
      this.startX = e.clientX
      this.isDragging = true
      this.deltaX = 0
      document.addEventListener('mousemove', this.onMouseMove)
      document.addEventListener('mouseup', this.onMouseUp)
    },
    onMouseMove: function (e) {
      if (!this.isDragging) return
      this.deltaX = e.clientX - this.startX
    },
    onMouseUp: function () {
      document.removeEventListener('mousemove', this.onMouseMove)
      document.removeEventListener('mouseup', this.onMouseUp)
      this.finishDrag()
    },
    onDocumentClick: function (e) {
      if (this.showTooltip && !this.$el.querySelector('.card-carousel__info-wrap').contains(e.target)) {
        this.showTooltip = false
      }
    },
    finishDrag: function () {
      this.isDragging = false
      var threshold = this.cardWidth * 0.15

      if (this.deltaX < -threshold && this.activeIndex < this.items.length - 1) {
        this.activeIndex++
      } else if (this.deltaX > threshold && this.activeIndex > 0) {
        this.activeIndex--
      }

      this.deltaX = 0
    },
    getDday: function (dateString) {
      var inputDate = new Date(dateString)
      var today = new Date()
      inputDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      var diffTime = inputDate - today
      var diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      if (diffDays === 0) return 'D-Day'
      if (diffDays > 0) return 'D-' + diffDays
      return 'D+' + Math.abs(diffDays)
    },
    getDdayClass: function (dateString) {
      var dday = this.getDday(dateString)
      return dday.indexOf('+') > -1 ? 'card-carousel__badge card-carousel__badge--plus' : 'card-carousel__badge'
    },
    formatDate: function (dateString) {
      var date = new Date(dateString)
      var year = date.getFullYear()
      var month = ('0' + (date.getMonth() + 1)).slice(-2)
      var day = ('0' + date.getDate()).slice(-2)
      return year + '. ' + month + '. ' + day
    },
    convertType: function (type) {
      if (type === 'electrode') return '전극'
      if (type === 'expendable') return '당뇨 소모품'
      if (type === 'catheter') return '카테터'
      return ''
    },
  },
}
</script>

<style scoped>
.card-carousel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card-carousel__header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
}

.card-carousel__info-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.card-carousel__info-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  margin-left: 8px;
}

.card-carousel__tooltip {
  position: absolute;
  top: calc(100% + 8px);
  left: calc(50% + 4px);
  transform: translateX(-40%);
  display: flex;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  background: rgba(24, 24, 28, 0.90);
  white-space: pre-line;
  z-index: 10;
  width: max-content;
  max-width: 260px;
}

.card-carousel__tooltip-arrow {
  position: absolute;
  bottom: 100%;
  left: 40%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid rgba(24, 24, 28, 0.90);
}

.card-carousel__viewport {
  overflow: hidden;
}

.card-carousel__track {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.card-carousel__card {
  flex: none;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  border-radius: 16px;
  padding: 20px;
  box-sizing: border-box;
  border: 1px solid #F2F4F6;
}

.card-carousel__card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.card-carousel__name {
  color: var(--color-gray-900);
}

.card-carousel__badge {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(86, 130, 239, 0.10);
  color: var(--color-blue-500);
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
}

.card-carousel__badge--plus {
  background: #FFF0E8;
  color: #FC7E2F;
}

.card-carousel__purchase {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.card-carousel__purchase + .card-carousel__purchase {
  margin-top: 16px;
}

.card-carousel__purchase-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-carousel__type {
  color: var(--Color-Gray-600, #6B7684);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
}

.card-carousel__date {
  color: var(--Color-Gray-800, #333D4B);
  text-align: right;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
}

.card-carousel__empty {
  display: flex;
  padding: 20px 16px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-radius: 12px;
  background: #CCE2F5;
  margin: 0 16px;
}

.card-carousel__empty-icon {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/new/organisms/CardCarousel.vue